import { useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { getTraitDisplayName, getTraitDescription } from "@/lib/utils/trait-names";
import { getTraitIconPath } from "@/lib/utils/trait-icons";
import { cn } from "@/lib/utils";

interface TraitToggleProps {
  traitName: string;
  isActive: boolean;
  activationType: 'auto' | 'manual' | 'unknown';
  activationReason?: string;
  effects: string[];
  override: boolean | undefined;
  onToggle: (traitName: string) => void;
  isSource: boolean;
}

export function TraitToggle({
  traitName,
  isActive,
  activationType,
  activationReason,
  effects,
  override,
  onToggle,
  isSource
}: TraitToggleProps) {
  const [imageError, setImageError] = useState(false);
  
  const getStatusColor = () => {
    if (!isActive) return 'secondary';
    if (activationType === 'manual') return 'default';
    if (activationType === 'unknown') return 'destructive';
    return 'default';
  };
  
  const getStatusText = () => {
    if (override === true) return 'Forced ON';
    if (override === false) return 'Forced OFF';
    if (activationType === 'unknown') return 'Unknown condition';
    if (isActive) return 'Active';
    return 'Inactive';
  };
  
  const canToggle = isSource || activationType !== 'unknown';
  
  const displayName = getTraitDisplayName(traitName);
  const description = getTraitDescription(traitName);
  const iconPath = getTraitIconPath(traitName);
  
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div 
          className={cn(
            "inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md border cursor-pointer transition-all",
            isActive && "bg-primary/10 border-primary/40",
            !isActive && "bg-muted/30 border-muted-foreground/20",
            !canToggle && "opacity-50 cursor-not-allowed",
            activationType === 'unknown' && "border-orange-500/40"
          )}
          onClick={() => canToggle && onToggle(traitName)}
        >
          {/* Trait Icon */}
          <div className="w-5 h-5 rounded bg-pink-500/20 border border-pink-500/40 flex items-center justify-center overflow-hidden flex-shrink-0">
            {iconPath && !imageError ? (
              <img
                src={iconPath}
                alt={displayName}
                className="w-full h-full object-contain"
                style={{
                  filter: 'var(--trait-icon-filter)'
                }}
                onError={() => setImageError(true)}
              />
            ) : (
              <span className="text-[10px] text-pink-500 font-bold">
                {traitName.replace('_', '').charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          
          {/* Trait Name - abbreviated on small screens */}
          <span className="text-xs font-medium whitespace-nowrap hidden sm:inline">
            {displayName}
          </span>
          
          {/* Compact Toggle Switch */}
          <Switch
            checked={isActive}
            onCheckedChange={() => canToggle && onToggle(traitName)}
            disabled={!canToggle}
            className="h-4 w-7 data-[state=checked]:bg-primary"
            onClick={(e) => e.stopPropagation()}
          />
          
          {/* Small status indicator badge (only show if overridden or unknown) */}
          {(override !== undefined || activationType === 'unknown') && (
            <div className={cn(
              "w-1.5 h-1.5 rounded-full flex-shrink-0",
              override === true && "bg-green-500",
              override === false && "bg-red-500",
              activationType === 'unknown' && override === undefined && "bg-orange-500"
            )} />
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm" side="top">
        <div className="space-y-2">
          {/* Header with status */}
          <div className="flex items-center justify-between gap-2 pb-2 border-b">
            <div className="font-semibold">{displayName}</div>
            <Badge variant={getStatusColor()} className="text-xs">
              {getStatusText()}
            </Badge>
          </div>
          
          {/* Description */}
          <div className="text-sm">{description}</div>
          
          {/* Activation Reason */}
          {activationReason && (
            <div className="text-xs text-muted-foreground pt-2 border-t">
              <div className="font-medium mb-1">Activation:</div>
              <div>{activationReason}</div>
            </div>
          )}
          
          {/* Effects */}
          {effects.length > 0 && (
            <div className="text-xs pt-2 border-t">
              <div className="font-medium mb-1">Effects:</div>
              <ul className="list-disc list-inside space-y-0.5">
                {effects.map((effect, i) => (
                  <li key={i}>{effect}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Unknown condition warning */}
          {activationType === 'unknown' && (
            <div className="text-xs text-orange-500 pt-2 border-t flex items-center gap-1">
              <span>⚠️</span>
              <span>Activation condition unknown - toggle manually to test</span>
            </div>
          )}
          
          {/* Special Forces display-only warning */}
          {traitName === '_sf' && (
            <div className="text-xs text-blue-500 pt-2 border-t flex items-center gap-1">
              <span>ℹ️</span>
              <span className="font-medium">Display Only:</span>
              <span>This trait cannot be toggled for gameplay effect. Special Forces bonuses are automatically applied through veterancy.</span>
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

